import { formatLabel } from '../lib/partDisplay'

export type DisplaySlot = {
  role: string
  position: number | null
  label?: string | null
}

export type DialLayout = {
  openings: DisplaySlot[]
  required_movement_ids: string[]
}

export type MovementCapabilities = {
  displays: DisplaySlot[]
  movement_type: string | null
  crown_position: number | null
  pusher_positions: number[]
}

export type CaseGeometry = {
  crown_position: number | null
  pusher_positions: number[]
  case_style: string | null
}

export const COMPLICATION_ROLES = [
  'date',
  'day',
  'day_date',
  'small_seconds',
  'gmt_24h',
  'open_heart',
  'power_reserve',
  'chronograph_subdial',
  'chronograph_hour',
  'chronograph_minute',
  'moon_phase',
  'world_time',
] as const

export const CLOCK_POSITIONS = [3, 4, 4.5, 6, 9, 12] as const

export const CASE_STYLES = ['dress', 'field', 'diver', 'pilot', 'gmt', 'chronograph'] as const

export const MOVEMENT_TYPES = ['automatic', 'manual', 'quartz'] as const

const ROLES_WITH_POSITION = new Set([
  'date',
  'day',
  'day_date',
  'small_seconds',
  'open_heart',
  'chronograph_subdial',
  'moon_phase',
])

function listToCsv(values: string[]): string {
  return values.join(', ')
}

function csvToList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function floatsFromCsv(value: string): number[] {
  return value
    .split(',')
    .map((item) => parseFloat(item.trim()))
    .filter((n) => !Number.isNaN(n))
}

function emptyDialLayout(): DialLayout {
  return { openings: [], required_movement_ids: [] }
}

function emptyMovementCapabilities(): MovementCapabilities {
  return {
    displays: [],
    movement_type: null,
    crown_position: null,
    pusher_positions: [],
  }
}

function emptyCaseGeometry(): CaseGeometry {
  return { crown_position: null, pusher_positions: [], case_style: null }
}

export function parseDialLayout(raw: unknown): DialLayout {
  if (!raw || typeof raw !== 'object') return emptyDialLayout()
  const obj = raw as Record<string, unknown>
  const openings = Array.isArray(obj.openings)
    ? obj.openings.map((slot) => {
        const s = slot as Record<string, unknown>
        return {
          role: typeof s.role === 'string' ? s.role : 'date',
          position: typeof s.position === 'number' ? s.position : null,
          label: typeof s.label === 'string' ? s.label : null,
        }
      })
    : []
  const required_movement_ids = Array.isArray(obj.required_movement_ids)
    ? obj.required_movement_ids.filter((id): id is string => typeof id === 'string')
    : []
  return { openings, required_movement_ids }
}

export function parseMovementCapabilities(raw: unknown): MovementCapabilities {
  if (!raw || typeof raw !== 'object') return emptyMovementCapabilities()
  const obj = raw as Record<string, unknown>
  const displays = Array.isArray(obj.displays)
    ? obj.displays.map((slot) => {
        const s = slot as Record<string, unknown>
        return {
          role: typeof s.role === 'string' ? s.role : 'date',
          position: typeof s.position === 'number' ? s.position : null,
          label: typeof s.label === 'string' ? s.label : null,
        }
      })
    : []
  return {
    displays,
    movement_type: typeof obj.movement_type === 'string' ? obj.movement_type : null,
    crown_position: typeof obj.crown_position === 'number' ? obj.crown_position : null,
    pusher_positions: Array.isArray(obj.pusher_positions)
      ? obj.pusher_positions.filter((p): p is number => typeof p === 'number')
      : [],
  }
}

export function parseCaseGeometry(raw: unknown): CaseGeometry {
  if (!raw || typeof raw !== 'object') return emptyCaseGeometry()
  const obj = raw as Record<string, unknown>
  return {
    crown_position: typeof obj.crown_position === 'number' ? obj.crown_position : null,
    pusher_positions: Array.isArray(obj.pusher_positions)
      ? obj.pusher_positions.filter((p): p is number => typeof p === 'number')
      : [],
    case_style: typeof obj.case_style === 'string' ? obj.case_style : null,
  }
}

type SlotListEditorProps = {
  slots: DisplaySlot[]
  onChange: (slots: DisplaySlot[]) => void
  addLabel?: string
}

function SlotListEditor({ slots, onChange, addLabel = 'Add opening' }: SlotListEditorProps) {
  const addSlot = () => {
    onChange([...slots, { role: 'date', position: 3, label: null }])
  }

  const updateSlot = (index: number, patch: Partial<DisplaySlot>) => {
    onChange(slots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)))
  }

  const removeSlot = (index: number) => {
    onChange(slots.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="catalog-slot-list">
        {slots.map((slot, index) => (
          <div key={index} className="catalog-slot-row">
            <select
              value={slot.role}
              onChange={(event) => updateSlot(index, { role: event.target.value })}
            >
              {COMPLICATION_ROLES.map((role) => (
                <option key={role} value={role}>{formatLabel(role)}</option>
              ))}
            </select>
            {ROLES_WITH_POSITION.has(slot.role) ? (
              <select
                value={slot.position ?? ''}
                onChange={(event) =>
                  updateSlot(index, {
                    position: event.target.value ? parseFloat(event.target.value) : null,
                  })
                }
              >
                <option value="">No position</option>
                {CLOCK_POSITIONS.map((pos) => (
                  <option key={pos} value={pos}>{pos}H</option>
                ))}
              </select>
            ) : null}
            <button type="button" className="catalog-slot-remove" onClick={() => removeSlot(index)}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn-secondary btn-small" onClick={addSlot}>
        {addLabel}
      </button>
    </div>
  )
}

type CatalogComplicationEditorProps = {
  partType: string
  dialLayout: DialLayout
  movementCapabilities: MovementCapabilities
  caseGeometry: CaseGeometry
  onDialLayoutChange: (layout: DialLayout) => void
  onMovementCapabilitiesChange: (caps: MovementCapabilities) => void
  onCaseGeometryChange: (geometry: CaseGeometry) => void
}

export function CatalogComplicationEditor({
  partType,
  dialLayout,
  movementCapabilities,
  caseGeometry,
  onDialLayoutChange,
  onMovementCapabilitiesChange,
  onCaseGeometryChange,
}: CatalogComplicationEditorProps) {
  if (partType === 'dial') {
    const noOpenings = dialLayout.openings.length === 0
    return (
      <div>
        <div className="catalog-toggle-row">
          <input
            type="checkbox"
            checked={noOpenings}
            onChange={(event) => {
              if (event.target.checked) {
                onDialLayoutChange({ ...dialLayout, openings: [] })
              } else {
                onDialLayoutChange({
                  ...dialLayout,
                  openings: [{ role: 'date', position: 3, label: null }],
                })
              }
            }}
          />
          <span>No openings / no-date dial</span>
        </div>
        {!noOpenings ? (
          <SlotListEditor
            slots={dialLayout.openings}
            onChange={(openings) => onDialLayoutChange({ ...dialLayout, openings })}
            addLabel="Add opening"
          />
        ) : null}
        <label style={{ marginTop: '12px' }}>
          Required movement IDs (comma-separated)
          <input
            value={listToCsv(dialLayout.required_movement_ids)}
            onChange={(event) =>
              onDialLayoutChange({
                ...dialLayout,
                required_movement_ids: csvToList(event.target.value),
              })
            }
          />
        </label>
      </div>
    )
  }

  if (partType === 'movement') {
    return (
      <div className="catalog-form-section">
        <SlotListEditor
          slots={movementCapabilities.displays}
          onChange={(displays) =>
            onMovementCapabilitiesChange({ ...movementCapabilities, displays })
          }
          addLabel="Add display"
        />
        <div className="catalog-form-grid" style={{ marginTop: '16px' }}>
          <label>
            Movement type
            <select
              value={movementCapabilities.movement_type ?? ''}
              onChange={(event) =>
                onMovementCapabilitiesChange({
                  ...movementCapabilities,
                  movement_type: event.target.value || null,
                })
              }
            >
              <option value="">Unknown</option>
              {MOVEMENT_TYPES.map((type) => (
                <option key={type} value={type}>{formatLabel(type)}</option>
              ))}
            </select>
          </label>
          <label>
            Crown position
            <input
              type="number"
              value={movementCapabilities.crown_position ?? ''}
              onChange={(event) =>
                onMovementCapabilitiesChange({
                  ...movementCapabilities,
                  crown_position: event.target.value ? parseInt(event.target.value, 10) : null,
                })
              }
            />
          </label>
          <label>
            Pusher positions (comma-separated)
            <input
              value={listToCsv(movementCapabilities.pusher_positions.map(String))}
              onChange={(event) =>
                onMovementCapabilitiesChange({
                  ...movementCapabilities,
                  pusher_positions: floatsFromCsv(event.target.value),
                })
              }
            />
          </label>
        </div>
      </div>
    )
  }

  if (partType === 'case') {
    return (
      <div className="catalog-form-grid">
        <label>
          Case style
          <select
            value={caseGeometry.case_style ?? ''}
            onChange={(event) =>
              onCaseGeometryChange({
                ...caseGeometry,
                case_style: event.target.value || null,
              })
            }
          >
            <option value="">Unknown</option>
            {CASE_STYLES.map((style) => (
              <option key={style} value={style}>{formatLabel(style)}</option>
            ))}
          </select>
        </label>
        <label>
          Crown position
          <input
            type="number"
            value={caseGeometry.crown_position ?? ''}
            onChange={(event) =>
              onCaseGeometryChange({
                ...caseGeometry,
                crown_position: event.target.value ? parseInt(event.target.value, 10) : null,
              })
            }
          />
        </label>
        <label>
          Pusher positions (comma-separated)
          <input
            value={listToCsv(caseGeometry.pusher_positions.map(String))}
            onChange={(event) =>
              onCaseGeometryChange({
                ...caseGeometry,
                pusher_positions: floatsFromCsv(event.target.value),
              })
            }
          />
        </label>
      </div>
    )
  }

  return (
    <p className="catalog-hint">
      No structured complication editor for {partType} parts. Use Advanced JSON if needed.
    </p>
  )
}
